/**
 * Permission codes the authority screens declare an interest in
 * (`internal/authority/permissions.go`, design §6.1).
 *
 * The split between them is not bookkeeping — it is the segregation of duties
 * the matrix exists to enforce, and §6.1 registers three of these pairs as
 * declared conflicts:
 *
 *  - `matrix.edit` ⊗ `matrix.activate` — whoever writes the rule that names the
 *    approver must not be the one who puts it in force. The server refuses
 *    `ERR_DUAL_CONTROL` when the activator is the submitter, and the editor
 *    says so before the button rather than after.
 *  - `org.position.manage` ⊗ `matrix.activate` — otherwise one person can create
 *    a position, assign themselves to it, and activate a matrix that routes to
 *    it.
 *  - `emergency.approve` ⊗ `emergency.ratify` — ratification by the emergency
 *    approver is not ratification.
 *
 * Every route in `AUTHORITY_ROUTES` is gated on a **read** code, so an Auditor
 * can open the matrix, the delegations and the absences and change none of them.
 * Actions are gated on the action.
 */

export const AUTHORITY_PERMISSIONS = {
  matrixRead: 'authority.matrix.read',
  matrixEdit: 'authority.matrix.edit',
  /** Step-up MFA server-side, and refused to the submitter. */
  matrixActivate: 'authority.matrix.activate',

  delegationRead: 'authority.delegation.read',
  delegationManage: 'authority.delegation.manage',

  absenceRead: 'authority.absence.read',
  absenceManage: 'authority.absence.manage',

  materialityManage: 'authority.materiality.manage',

  /** Step-up MFA server-side (XFN-AP-010). */
  emergencyApprove: 'authority.emergency.approve',
  emergencyRatify: 'authority.emergency.ratify',

  /** Read positions to fill every approver picker on these screens. */
  positionRead: 'org.position.read',
  /** Read reason codes to fill the governed delegation-revoke picker. */
  reasonCodeRead: 'rules.reason.read',
} as const

export type AuthorityPermission =
  (typeof AUTHORITY_PERMISSIONS)[keyof typeof AUTHORITY_PERMISSIONS]
